import { useEffect, useMemo, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, FolderTree } from "lucide-react";
import { useTeamTree } from "@/hooks/queries/use-teams";
import type { TeamLevel, TeamTreeNode } from "@/lib/api/types";
import { getTeamLevelLabel } from "@/lib/team-hierarchy";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

interface HierarchicalTeamPickerProps {
  value?: string | null;
  onChange: (teamId: string | null) => void;
  placeholder?: string;
  allTeamsLabel?: string;
  disabled?: boolean;
  className?: string;
}

interface FlatTeam {
  id: string;
  name: string;
  fullPath: string;
  key?: string | undefined;
  level?: TeamLevel | null | undefined;
  depth: number;
}

interface TeamGroup {
  id: string;
  heading: string;
  teams: FlatTeam[];
}

const DEPTH_INDENT_CLASSES = ["", "pl-3", "pl-6", "pl-9", "pl-12"] as const;

function buildFullPath(team: TeamTreeNode): string {
  if (team.path?.length) {
    return team.path.map((node) => node.name).join(" / ");
  }

  return team.name;
}

function flattenSubtree(node: TeamTreeNode): FlatTeam[] {
  const current: FlatTeam = {
    id: node.id,
    name: node.name,
    fullPath: buildFullPath(node),
    key: node.key,
    level: node.level,
    depth: Math.max((node.path?.length ?? 1) - 1, 0),
  };

  return [current, ...node.children.flatMap((child) => flattenSubtree(child))];
}

function buildGroups(nodes: TeamTreeNode[]): TeamGroup[] {
  return nodes.map((node) => ({
    id: node.id,
    heading: node.name,
    teams: flattenSubtree(node),
  }));
}

function matchesSearch(team: FlatTeam, query: string): boolean {
  if (!query) {
    return true;
  }

  const normalizedQuery = query.toLowerCase();

  return (
    team.name.toLowerCase().includes(normalizedQuery) ||
    team.key?.toLowerCase().includes(normalizedQuery) ||
    team.fullPath.toLowerCase().includes(normalizedQuery)
  );
}

export function HierarchicalTeamPicker({
  value = null,
  onChange,
  placeholder = "Select team…",
  allTeamsLabel = "All Teams",
  disabled = false,
  className,
}: HierarchicalTeamPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: teamTree = [], isLoading } = useTeamTree();

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      setSearch("");
    }
  }, [open]);

  const teamGroups = useMemo(() => buildGroups(teamTree), [teamTree]);

  const allTeams = useMemo(
    () => teamGroups.flatMap((group) => group.teams),
    [teamGroups],
  );

  const selectedTeam = useMemo(
    () => allTeams.find((team) => team.id === value),
    [allTeams, value],
  );

  const filteredGroups = useMemo(() => {
    const trimmedSearch = search.trim();

    return teamGroups
      .map((group) => ({
        ...group,
        teams: group.teams.filter((team) => matchesSearch(team, trimmedSearch)),
      }))
      .filter((group) => group.teams.length > 0);
  }, [search, teamGroups]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          className={cn(
            "w-[260px] items-center justify-between gap-2 px-3 text-left font-normal",
            !selectedTeam && "text-muted-foreground",
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            <FolderTree className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{selectedTeam ? selectedTeam.fullPath : allTeamsLabel || placeholder}</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </Button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 w-[min(32rem,var(--radix-popover-trigger-width))] rounded-lg border bg-popover shadow-md outline-none animate-in fade-in-0 zoom-in-95"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <Command shouldFilter={false}>
            <CommandInput
              ref={inputRef}
              placeholder="Search teams or hierarchy…"
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandGroup>
                <CommandItem
                  value="all-teams"
                  onSelect={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                  className="items-start gap-3 py-2"
                >
                  <Check
                    className={cn(
                      "mt-0.5 h-4 w-4 shrink-0",
                      value == null ? "opacity-100 text-primary" : "opacity-0",
                    )}
                  />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{allTeamsLabel}</div>
                    <div className="truncate text-xs text-muted-foreground">Clear the team filter</div>
                  </div>
                </CommandItem>
              </CommandGroup>

              {filteredGroups.length === 0 ? (
                <CommandEmpty>
                  {isLoading ? "Loading teams…" : "No teams found."}
                </CommandEmpty>
              ) : (
                filteredGroups.map((group) => (
                  <CommandGroup key={group.id} heading={group.heading}>
                    {group.teams.map((team) => (
                      <CommandItem
                        key={team.id}
                        value={`${team.fullPath} ${team.key ?? ""}`}
                        onSelect={() => {
                          onChange(team.id);
                          setOpen(false);
                        }}
                        className="items-start gap-3 py-2"
                      >
                        <Check
                          className={cn(
                            "mt-0.5 h-4 w-4 shrink-0",
                            value === team.id ? "opacity-100 text-primary" : "opacity-0",
                          )}
                        />
                        <div
                          className={cn(
                            "min-w-0 flex-1",
                            DEPTH_INDENT_CLASSES[Math.min(team.depth, DEPTH_INDENT_CLASSES.length - 1)],
                          )}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="truncate text-sm font-medium">{team.name}</div>
                            {team.level ? (
                              <span className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                                {getTeamLevelLabel(team.level)}
                              </span>
                            ) : null}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {team.fullPath}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))
              )}
            </CommandList>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
